
import { defineComponent, computed, onMounted, ref, onUnmounted, ComponentPublicInstance } from "vue";
import {
  FridgeDetailsMylist,
  FridgeDetailsCoolingAssistSettingList,
  FridgeDetailsMyAssistSettingItem,
  FridgeCoolingAssistSettingItem,
  FRIDGE_DETAILS_COOLING_MODE,
  FRIDGE_DETAILS_COOLING_MODE_DISPLAY_STRING
} from "@/types/fridgeDetails";
import NativeClient, { TitleInfo } from "@/client/native-client";
import store from "@/store";
import router from "@/router";
import { useApiErrorHandler } from "@/use/use-api-error-handler";
import { useApiErrorDialog } from "@/use/use-api-error-dialog";
import { useLog } from "@/use/use-log";
import { PAGE_ID } from "@/const/log-const";
import { ApiError, BeaconLogInfo, UpdatePageInfo } from "@/types/common";
import { COMMON_LIMIT_NUM } from "@/const/limit_const"; 
import { COMMON_MESSAGE_STRING } from "@/const/message_const";
import CommonUtil, { translateYYYYMMDDToJapan } from "@/utils/common-utils";
import FridgeDetailsRadioButton from "@/components/fridgeDetails/FridgeDetailsRadioButton.vue";
import FridgeDetailsDrumPicker from "@/components/fridgeDetails/FridgeDetailsDrumPicker.vue";
import CommonDesignDialog from "@/components/common/CommonDesignDialog.vue";

export default defineComponent({
  name: "FridgeMyAssistEditingPage",
  components: { FridgeDetailsRadioButton, FridgeDetailsDrumPicker, CommonDesignDialog },
  setup() {
    const { setLogId, outputPageLogs } = useLog();

    // queryを取得
    let isFromNative = false;
    const settingIdString = router.currentRoute.value.query.id;
    const queryMode = router.currentRoute.value.query.mode;
    const queryMinutes = router.currentRoute.value.query.minutes;
    const querySeconds = router.currentRoute.value.query.seconds;
    NativeClient.sendDebugLog("FridgeMyAssistEditingPage setup() query id = " + settingIdString + ", mode = " + queryMode + ", minutes = " + queryMinutes + ", seconds = " + querySeconds);

    // タイトル行の設定
    if (settingIdString == null) {
      // ID未指定の場合は、追加
      NativeClient.sendDebugLog("FridgeMyAssistEditingPage setup() append mode");
      NativeClient.setTitle(new TitleInfo(false, "マイリストを追加"));
    } else {
      // ID指定の場合は、編集
      NativeClient.sendDebugLog("FridgeMyAssistEditingPage setup() edit mode");
      NativeClient.setTitle(new TitleInfo(false, "マイリストを編集"));
    }
    CommonUtil.scroll({ top: 0, left: 0 });

    // 定数
    const textMaxLength = 9;

    // 表示更新のためのリアクティブ
    const updateDisplayKey = ref(0);
    const updateDisplay = () => {
      if (updateDisplayKey.value == 100) {
        updateDisplayKey.value = 0;
      } else {
        updateDisplayKey.value++;
      }
    };

    // 有効／無効
    const isValidContents = computed(() => {
      const settings: FridgeDetailsCoolingAssistSettingList | null = store.getters["fridgeDetails/getCoolingAssistSettings"];
      if (settings == null) {
        return false;
      }
      return true;
    });

    // テキスト
    const textInput = ref<ComponentPublicInstance<HTMLInputElement>>();

    // 時間の設定上下限
    class FridgeDetailsMyAssistLimitParameter {
      lowerLimitMinutes: number;
      upperLimitMinutes: number;
      lowerLimitSeconds: number;
      upperLimitSeconds: number;
      isValidSeconds: boolean;
      constructor() {
        this.lowerLimitMinutes = 0;
        this.upperLimitMinutes = 0;
        this.lowerLimitSeconds = 0;
        this.upperLimitSeconds = 0;
        this.isValidSeconds = false;
      }
    }

    // 設定値
    const getOriginalSettings = (id: number) => {
      const data: FridgeDetailsMylist | null = store.getters["fridgeDetails/getMyAssist"];
      if (data == null) {
        return null;
      }
      const index = data.mylist_list.findIndex(item => item.mylist_id == id);
      if (index == -1) {
        return null;
      }
      return data.mylist_list[index];
    };
    const getLimitParameter = (mode: string) => {
      const settings: FridgeDetailsCoolingAssistSettingList | null = store.getters["fridgeDetails/getCoolingAssistSettings"];
      if (settings == null) {
        return null;
      }
      const index = settings.cooloven_setting_list.findIndex(item => item.mode == mode);
      if (index != -1) { 
        const limitParameter = new FridgeDetailsMyAssistLimitParameter();
        limitParameter.lowerLimitMinutes = settings.cooloven_setting_list[index].lower_limit;
        limitParameter.upperLimitMinutes = settings.cooloven_setting_list[index].upper_limit;
        if (mode == FRIDGE_DETAILS_COOLING_MODE.QUENCH && settings.cooloven_in_seconds) {
          limitParameter.lowerLimitSeconds = settings.cooloven_setting_list[index].lower_limit_second;
          limitParameter.upperLimitSeconds = settings.cooloven_setting_list[index].upper_limit_second;
        }
        return limitParameter;
      }
      return null;
    };
    const getDefaultParameter = (mode: string) => {
      const settings: FridgeDetailsCoolingAssistSettingList | null = store.getters["fridgeDetails/getCoolingAssistSettings"];
      if (settings == null) {
        return null;
      }
      const index = settings.cooloven_setting_list.findIndex(item => item.mode == mode);
      if (index != -1) { 
        return settings.cooloven_setting_list[index].default_time;
      }
      return null;
    };

    // 操作の保存
    const myAssistBuffer: Array<FridgeCoolingAssistSettingItem> = [];
    const getModeInitialItem = (original: FridgeCoolingAssistSettingItem | null, mode: string) => {
      const defaultValue = getDefaultParameter(mode);
      if (original != null && original.cooloven_mode == mode) {
        return original;
      }
      const temp = new FridgeCoolingAssistSettingItem();
      temp.cooloven_mode = mode;
      if (defaultValue != null) {
        temp.cooloven_time = defaultValue;
      }
      temp.cooloven_second = 0;
      return temp;
    };
    const setMyAssistBuffer = () => {
      if (settingIdString != undefined && settingIdString != null) {
        const id = Number(settingIdString);
        const original = getOriginalSettings(id);
        if (original != null) {
          const quenchInitial = getModeInitialItem(original, FRIDGE_DETAILS_COOLING_MODE.QUENCH);
          myAssistBuffer.push(quenchInitial);
          const coldInitial = getModeInitialItem(original, FRIDGE_DETAILS_COOLING_MODE.COLD);
          myAssistBuffer.push(coldInitial);
          const frozenInitial = getModeInitialItem(original, FRIDGE_DETAILS_COOLING_MODE.FROZEN);
          myAssistBuffer.push(frozenInitial);
        }
      } else {
        const quenchInitial = getModeInitialItem(null, FRIDGE_DETAILS_COOLING_MODE.QUENCH);
        const coldInitial = getModeInitialItem(null, FRIDGE_DETAILS_COOLING_MODE.COLD);
        const frozenInitial = getModeInitialItem(null, FRIDGE_DETAILS_COOLING_MODE.FROZEN);
        if (queryMode != undefined && queryMode != null) {
          switch (queryMode) {
            case FRIDGE_DETAILS_COOLING_MODE.QUENCH:
              if (queryMinutes != undefined && queryMinutes != null) {
                quenchInitial.cooloven_time = Number(queryMinutes);
                if (querySeconds != undefined && querySeconds != null) {
                  quenchInitial.cooloven_second = Number(querySeconds);
                }
              }
              break;
            case FRIDGE_DETAILS_COOLING_MODE.COLD:
              if (queryMinutes != undefined && queryMinutes != null) {
                coldInitial.cooloven_time = Number(queryMinutes);
              }
              break;
            case FRIDGE_DETAILS_COOLING_MODE.FROZEN:
              if (queryMinutes != undefined && queryMinutes != null) {
                frozenInitial.cooloven_time = Number(queryMinutes);
              }
              break;
            default:
              break;
          }
        }
        myAssistBuffer.push(quenchInitial);
        myAssistBuffer.push(coldInitial);
        myAssistBuffer.push(frozenInitial);
      }
    };

    // 現在の設定値
    const currentSetting = ref(new FridgeDetailsMyAssistSettingItem());
    const currentLimitParameter = ref(new FridgeDetailsMyAssistLimitParameter());
    const setCurrentParameter = (mode: string) => {
      const settings: FridgeDetailsCoolingAssistSettingList | null = store.getters["fridgeDetails/getCoolingAssistSettings"];
      if (settings == null) {
        return;
      }
      currentSetting.value.cooloven_mode = mode;
      const item = myAssistBuffer.find(element => element.cooloven_mode == mode);
      NativeClient.sendDebugLog("FridgeMyAssistEditingPage setCurrentParameter() myAssistBuffer = " + JSON.stringify(myAssistBuffer));
      if (item != undefined && item != null) {
        NativeClient.sendDebugLog("FridgeMyAssistEditingPage setCurrentParameter() item found = " + JSON.stringify(item));
        currentSetting.value.cooloven_time = item.cooloven_time;
        if (item.cooloven_second != undefined) {
          currentSetting.value.cooloven_second = item.cooloven_second;
        } else {
          currentSetting.value.cooloven_second = 0;
        }
        if (settings.cooloven_in_seconds && mode == FRIDGE_DETAILS_COOLING_MODE.QUENCH) {
          NativeClient.sendDebugLog("FridgeMyAssistEditingPage setCurrentParameter() isValidSeconds true");
          currentSetting.value.valid_second = true;
        } else {
          NativeClient.sendDebugLog("FridgeMyAssistEditingPage setCurrentParameter() isValidSeconds false");
          currentSetting.value.valid_second = false;
        }
      } else if (isFromNative) {
        if (mode == queryMode!) {
          currentSetting.value.cooloven_mode = mode;
          if (queryMinutes != undefined && queryMinutes != null) {
            currentSetting.value.cooloven_time = Number(queryMinutes);
            if (mode == FRIDGE_DETAILS_COOLING_MODE.QUENCH && querySeconds != undefined && querySeconds != null) {
              currentSetting.value.cooloven_second = Number(querySeconds);
            }
            if (settings.cooloven_in_seconds && mode == FRIDGE_DETAILS_COOLING_MODE.QUENCH) {
              NativeClient.sendDebugLog("FridgeMyAssistEditingPage setCurrentParameter() isFromNative isValidSeconds true");
              currentSetting.value.valid_second = true;
            } else {
              NativeClient.sendDebugLog("FridgeMyAssistEditingPage setCurrentParameter() isFromNative isValidSeconds false");
              currentSetting.value.valid_second = false;
            }
          } else {
            const defaultSettings = getModeInitialItem(null, mode);
            currentSetting.value.cooloven_time = defaultSettings!.cooloven_time;
            if (defaultSettings!.cooloven_second != null) {
              currentSetting.value.cooloven_second = defaultSettings!.cooloven_second;
            }
          }
        }
      }

      if (settingIdString != undefined && settingIdString != null) {
        currentSetting.value.mylist_id = Number(settingIdString);
      } 

      const limitParameter = getLimitParameter(mode);
      if (limitParameter != null) {
        currentLimitParameter.value.lowerLimitMinutes = limitParameter.lowerLimitMinutes;
        currentLimitParameter.value.upperLimitMinutes = limitParameter.upperLimitMinutes;
        currentLimitParameter.value.lowerLimitSeconds = limitParameter.lowerLimitSeconds;
        currentLimitParameter.value.upperLimitSeconds = limitParameter.upperLimitSeconds;
      }
      NativeClient.sendDebugLog("FridgeMyAssistEditingPage setCurrentParameter() currentSetting = " + JSON.stringify(currentSetting.value));
    };

    // 初期設定
    const initialCurrentSetting = () => {
      if (settingIdString == undefined || settingIdString == null) {
        NativeClient.sendDebugLog("FridgeMyAssistEditingPage initialCurrentSetting() regist mode");
        // 新規登録
        if (queryMode == undefined || queryMode == null) {
          // モードの指定なし
          setCurrentParameter(FRIDGE_DETAILS_COOLING_MODE.QUENCH);
        } else {
          // モード指定ありならば、Nativeから
          NativeClient.sendDebugLog("FridgeMyAssistEditingPage initialCurrentSetting() from Native mode = " + queryMode);
          isFromNative = true;
          switch (queryMode) {
            case FRIDGE_DETAILS_COOLING_MODE.QUENCH:
              setCurrentParameter(FRIDGE_DETAILS_COOLING_MODE.QUENCH);
              break;
            case FRIDGE_DETAILS_COOLING_MODE.COLD:
              setCurrentParameter(FRIDGE_DETAILS_COOLING_MODE.COLD);
              break;
            case FRIDGE_DETAILS_COOLING_MODE.FROZEN:
              setCurrentParameter(FRIDGE_DETAILS_COOLING_MODE.FROZEN);
              break;
            default:
              setCurrentParameter(FRIDGE_DETAILS_COOLING_MODE.QUENCH);
              break;
          }
        }
      } else {
        NativeClient.sendDebugLog("FridgeMyAssistEditingPage initialCurrentSetting() update mode");
        const settingId = Number(settingIdString);
        const originalSetting = getOriginalSettings(settingId);
        if (originalSetting != null) {
          NativeClient.sendDebugLog("FridgeMyAssistEditingPage initialCurrentSetting() getOriginalSettings isValid");
          setCurrentParameter(originalSetting?.cooloven_mode);
          currentSetting.value.nickname = originalSetting.nickname;
        } else {
          setCurrentParameter(FRIDGE_DETAILS_COOLING_MODE.QUENCH);
        }
      }
    }

    // 定数
    const listTitleText = "リストタイトル（9文字まで）";
    const courseTitleText = "運転コース";
    const kindTitleLabels = [
      FRIDGE_DETAILS_COOLING_MODE_DISPLAY_STRING.QUENCH,
      FRIDGE_DETAILS_COOLING_MODE_DISPLAY_STRING.COLD, 
      FRIDGE_DETAILS_COOLING_MODE_DISPLAY_STRING.FROZEN
    ];
    const settingTimeTitleText = "運転時間";
    const saveButtonText = "保存する";

    // 運転コース
    const initialIndexKind = computed(() => {
      let result = 0;
      switch (currentSetting.value.cooloven_mode) {
        case FRIDGE_DETAILS_COOLING_MODE.QUENCH:
          result = 0;
          break;
        case FRIDGE_DETAILS_COOLING_MODE.COLD:
          result = 1;
          break;
        case FRIDGE_DETAILS_COOLING_MODE.FROZEN:
          result = 2;
          break;
        default:
          break;
      }
      return result;
    });

    // ドラム
    const selectTime =  (minutes: number, seconds: number) => {
      NativeClient.sendDebugLog("FridgeMyAssistEditingPage selectTime() minutes = " + minutes + ", seconds = " + seconds);
      currentSetting.value.cooloven_time = minutes;
      currentSetting.value.cooloven_second = seconds;
      const index = myAssistBuffer.findIndex(element => element.cooloven_mode == currentSetting.value.cooloven_mode);
      if (index != -1) {
        myAssistBuffer[index].cooloven_time = minutes;
        myAssistBuffer[index].cooloven_second = seconds;
      }

      // テキストが消えるので、復旧させる
      if (textInput.value != undefined && textInput.value != null) {
        currentSetting.value.nickname = textInput.value.value;
      }
    };

    // モード変更
    const kindChange = (index: number) => {
      let mode = FRIDGE_DETAILS_COOLING_MODE.QUENCH;
      switch (index) {
        case 0:
          mode = FRIDGE_DETAILS_COOLING_MODE.QUENCH;
          break;
        case 1:
          mode = FRIDGE_DETAILS_COOLING_MODE.COLD;
          break;
        case 2:
          mode = FRIDGE_DETAILS_COOLING_MODE.FROZEN;
          break;
        default:
          break;
      }
      setCurrentParameter(mode);

      // テキストが消えるので、復旧させる
      if (textInput.value != undefined && textInput.value != null) {
        currentSetting.value.nickname = textInput.value.value;
      }

      updateDisplay();
    };


    // 登録
    const registData = async () => {
      store.dispatch("common/startLoading");
      await NativeClient.getApiParams();
      await store.dispatch("fridgeDetails/registMyAssist", currentSetting.value),
      store.dispatch("common/stopLoading");

      // 正常終了すると、元の画面に戻る
      if (isFromNative) {
        // マイアシストのタブをマイリストに設定しておく（これにより、タブがマイリストに設定される）
        store.dispatch("fridgeDetails/updateCoolingAssistTopTab", 1);
        // Native側からの遷移であると、マイリストに遷移
        router.replace("/fridgeCoolingAssist");
      } else {
        router.go(-1);
      }
    };
    // 更新
    const updateData = async () => {
      store.dispatch("common/startLoading");
      await NativeClient.getApiParams();
      await store.dispatch("fridgeDetails/updateMyAssist", currentSetting.value),
      store.dispatch("common/stopLoading");

      // 正常終了すると、元の画面に戻る
      router.go(-1);
    };
    // 必要なパラメータ読み込み
    let isGettingParameter = false;
    const getParameter = async () => {
      NativeClient.sendDebugLog("FridgeMyAssistEditingPage getParameter() start");
      isGettingParameter = true;
      store.dispatch("common/startLoading");
      await NativeClient.getApiParams();
      await store.dispatch("fridgeDetails/getCoolingAssistSettings"),
      store.dispatch("common/stopLoading");
      isGettingParameter = false;
      NativeClient.sendDebugLog("FridgeMyAssistEditingPage getParameter() end");
      setMyAssistBuffer();
      initialCurrentSetting();
      updateDisplay();
    };

    // クーリングアシスト・ボタン
    const saveButtonAction = () => {
      NativeClient.sendDebugLog("FridgeMyAssistEditingPage settingButtonAction()");
      if (textInput.value != undefined && textInput.value != null) {
        if (textInput.value.value == null || textInput.value.value.length == 0) {
          currentSetting.value.nickname = "レシピ";
        } else {
          currentSetting.value.nickname = textInput.value.value;
        }
        if (settingIdString == undefined || settingIdString == null) {
          // 登録
          registData();
        } else {
          // 更新
          updateData();
        }
      }
    };

    // 表示の制御
    onMounted(() => {
      NativeClient.sendDebugLog("FridgeMyAssistEditingPage onMounted()");

      // ページIDは未決定
      // const logInfo = new BeaconLogInfo(PAGE_ID.FAVORITES_MYLIST, null, []);
      // NativeClient.sendViewedLog(logInfo);

      // 必要なパラメータが存在しなければ、取得
      const settings: FridgeDetailsCoolingAssistSettingList | null = store.getters["fridgeDetails/getCoolingAssistSettings"];
      if (settings == null) {
        getParameter();
      } else {
        setMyAssistBuffer();
        initialCurrentSetting();
        updateDisplay();
      }
    });

    //
    // APIリクエスト時のエラーハンドル
    //
    const {
      handleApiError,
      getApiErrorTitle,
      getApiErrorMessage
    } = useApiErrorHandler();
    const { closeDialog } = useApiErrorDialog();
    const typeApiError = ref(9);
    const retryButtonTitle = "リトライ";
    const okButtonTitle = "OK";
    // APIのエラー・ダイアログは、リトライが存在しないが、共通ダイアログのIFは残っているため、削除せず、そのまま置いておく
    const retryApiErrorHandler = () => {
      closeDialog();
      // if (isGettingParameter) {
      //   // パラメータ読み込み
      //   getParameter();
      // } else if (settingIdString == undefined || settingIdString == null) {
      //   // 登録
      //   registData();
      // } else {
      //   // 更新
      //   updateData();
      // }
    };
    const okApiErrorHandler = () => {
      closeDialog();
    };

    return {
      updateDisplayKey,
      isValidContents,
      listTitleText,
      textInput,
      textMaxLength,
      courseTitleText,
      kindTitleLabels,
      initialIndexKind,
      settingTimeTitleText,
      currentSetting,
      currentLimitParameter,
      selectTime,
      saveButtonText,
      saveButtonAction,
      kindChange,
      handleApiError,
      getApiErrorTitle,
      getApiErrorMessage,
      typeApiError,
      retryButtonTitle,
      okButtonTitle,
      retryApiErrorHandler,
      okApiErrorHandler
    };
  }
});
